import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buildBookingInFromBooking,
  fetchPreisBerechnung,
  fetchRechnungPreview,
  postDocument,
  updateBooking,
  downloadEmailDraft,
  type Booking,
  type PreisBerechnung,
  type RechnungPositionenResponse,
} from "@/api/bookingDetail";
import { getMailConfig } from "@/api/settings";
import { GRUPPENARTEN } from "@/api/newBooking";
import { Btn } from "@/components/Btn";
import { EurInput } from "@/components/EurInput";
import { MailPreviewModal } from "@/components/MailPreviewModal";
import { formatEuro } from "@/lib/format";

type Props = {
  bk: Booking;
  open: boolean;
  onClose: () => void;
  onToast: (msg: string, ok?: boolean) => void;
  hasRechnung?: boolean;
};

export function RechnungDialog({ bk, open, onClose, onToast, hasRechnung = false }: Props) {
  const queryClient = useQueryClient();
  const isStorno = bk.status === "Storniert";
  const isIntern = bk.status === "Intern";

  const [saison, setSaison] = useState<string>(bk.saison || "Sommer");
  const [gruppenart, setGruppenart] = useState<string>(bk.gruppenart || "");
  const [nachreinigungEur, setNachreinigungEur] = useState<number>(bk.nachreinigung_eur ?? 0);
  const [fehlteile, setFehlteile] = useState<string>(bk.rechnung_fehlteile ?? "");
  const [bemerkung, setBemerkung] = useState<string>(bk.rechnung_bemerkung ?? "");
  const [rabattProzent, setRabattProzent] = useState<number>(bk.rechnung_rabatt_prozent ?? 0);
  const [rabattGrund, setRabattGrund] = useState<string>(bk.rechnung_rabatt_grund ?? "");
  const [tarifPreis, setTarifPreis] = useState<PreisBerechnung | null>(null);
  const [showMailPreview, setShowMailPreview] = useState(false);
  const [showSmtpWarn, setShowSmtpWarn] = useState(false);
  const [rechnungErzeugt, setRechnungErzeugt] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [positionenData, setPositionenData] = useState<RechnungPositionenResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setPreviewHtml("");
      setPositionenData(null);
      return;
    }
    setSaison(bk.saison || "Sommer");
    setGruppenart(bk.gruppenart || "");
    setNachreinigungEur(bk.nachreinigung_eur ?? 0);
    setFehlteile(bk.rechnung_fehlteile ?? "");
    setBemerkung(bk.rechnung_bemerkung ?? "");
    setRabattProzent(bk.rechnung_rabatt_prozent ?? 0);
    setRabattGrund(bk.rechnung_rabatt_grund ?? "");
    setRechnungErzeugt(hasRechnung);
    if (!isStorno && !isIntern) {
      fetchPreisBerechnung(bk.id).then(setTarifPreis).catch(() => null);
    } else {
      setTarifPreis(null);
    }
  }, [open, bk, isStorno, isIntern, hasRechnung]);

  const loadPreview = useCallback(() => {
    if (!open) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const seq = ++seqRef.current;
    setPreviewLoading(true);
    fetchRechnungPreview(
      bk.id,
      {
        rechnung_bemerkung: bemerkung,
        nachreinigung_eur: nachreinigungEur,
        anzahlung_erhalten: bk.anzahlung_erhalten ?? false,
        anzahlung_betrag: bk.anzahlung_betrag ?? 100,
        rechnung_rabatt_prozent: rabattProzent,
        rechnung_fehlteile: fehlteile,
        saison,
        gruppenart: gruppenart || undefined,
      },
      ctrl.signal,
    )
      .then((res) => {
        if (seq !== seqRef.current) return; // veraltete Response verwerfen
        setPreviewHtml(res.html);
        setPositionenData(res.positionen);
      })
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name === "AbortError") return;
        // sonstige Fehler still ignorieren (kein Toast-Spam beim Tippen)
      })
      .finally(() => {
        if (seq === seqRef.current) setPreviewLoading(false);
      });
  }, [
    open, bk.id, bk.anzahlung_erhalten, bk.anzahlung_betrag,
    bemerkung, nachreinigungEur, rabattProzent, fehlteile, saison, gruppenart,
  ]);

  // Debounced Refetch bei jeder Eingabe.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadPreview, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, loadPreview]);

  // Tarif neu laden bei Saison-/Gruppenart-Wechsel (für keinPreis-Check + Save-Mutation).
  useEffect(() => {
    if (!open || isStorno || isIntern) return;
    fetchPreisBerechnung(bk.id).then(setTarifPreis).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saison, gruppenart]);

  useEffect(() => {
    if (!iframeRef.current || !previewHtml) return;
    const blob = new Blob([previewHtml], { type: "text/html; charset=utf-8" });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [previewHtml]);

  const berechneterPreis = tarifPreis?.ok ? tarifPreis.tarif_gesamt : null;
  const keinPreis = !isStorno && !isIntern
    && (!berechneterPreis || berechneterPreis <= 0);

  // Dirty-Check: unterscheiden sich die Dialog-Werte von den DB-Werten?
  // Wenn ja, ist die in der Ablage liegende Rechnung nicht mehr aktuell und
  // darf nicht versendet werden — der Nutzer muss zuerst neu speichern & erzeugen.
  const isDirty =
    saison !== (bk.saison || "Sommer") ||
    gruppenart !== (bk.gruppenart || "") ||
    nachreinigungEur !== (bk.nachreinigung_eur ?? 0) ||
    fehlteile !== (bk.rechnung_fehlteile ?? "") ||
    bemerkung !== (bk.rechnung_bemerkung ?? "") ||
    rabattProzent !== (bk.rechnung_rabatt_prozent ?? 0) ||
    rabattGrund !== (bk.rechnung_rabatt_grund ?? "");

  const positionen = positionenData?.positionen ?? [];
  const summen = useMemo(() => {
    if (!positionenData) return { zwischensumme: 0, rabattBetrag: 0, anzahlung: 0, gesamt: 0 };
    return {
      zwischensumme: positionenData.zwischensumme,
      rabattBetrag: positionenData.rabatt_betrag,
      anzahlung: positionenData.anzahlung_abzug,
      gesamt: positionenData.gesamtbetrag,
    };
  }, [positionenData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = buildBookingInFromBooking(bk, {
        rechnung_bemerkung: bemerkung,
        nachreinigung_eur: nachreinigungEur,
        rechnung_fehlteile: fehlteile,
        kurtaxe_betrag: null,
        anzahlung_betrag: bk.anzahlung_betrag ?? 100,
        anzahlung_erhalten: bk.anzahlung_erhalten ?? false,
        saison,
        gruppenart,
        rechnung_rabatt_prozent: rabattProzent,
        rechnung_rabatt_grund: rabattGrund,
      });
      await updateBooking(bk.id, body);
      return await postDocument(bk.id, "rechnung", {});
    },
    onSuccess: (res) => {
      onToast(res.detail);
      setRechnungErzeugt(true);
      queryClient.invalidateQueries({ queryKey: ["ablage", bk.id] });
      queryClient.invalidateQueries({ queryKey: ["booking", bk.id] });
    },
    onError: (err: Error) => onToast(err.message, false),
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const mailCfg = await getMailConfig();
      return mailCfg;
    },
    onSuccess: async (mailCfg) => {
      const modus = mailCfg.versand_modus;
      if (modus === "smtp_preview") {
        if (!mailCfg.smtp_host || !(mailCfg.smtp_from || mailCfg.smtp_user)) {
          setShowSmtpWarn(true);
          return;
        }
        setShowMailPreview(true);
      } else if (modus === "eml_download") {
        try { await downloadEmailDraft(bk.id, "rechnung"); } catch { /* still */ }
        onClose();
      } else {
        onClose();
      }
    },
    onError: (err: Error) => onToast(err.message, false),
  });

  if (!open) return null;

  return (
    <>
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(20,15,10,.5)",
        backdropFilter: "blur(4px)",
        display: "flex", flexDirection: "column",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          margin: "auto",
          width: "min(1200px, 97vw)",
          height: "min(860px, 94vh)",
          background: "var(--bg-page)",
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,.3)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: "12px 20px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-card)",
        }}>
          <span className="material-icons-outlined" style={{ fontSize: 22, color: "var(--brand-700)" }}>
            receipt_long
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Rechnung erstellen</div>
            <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
              {bk.buchungsreferenz || `#${bk.id}`} · {bk.gruppenname || bk.gastname}
            </div>
          </div>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={saveMutation.isPending}>
            Abbrechen
          </Btn>
          <Btn
            variant="primary"
            icon="save"
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || keinPreis}
          >
            {saveMutation.isPending ? "Wird erstellt …" : "Speichern & erzeugen"}
          </Btn>
          <Btn
            variant="secondary"
            icon="send"
            size="sm"
            onClick={() => sendMutation.mutate()}
            disabled={!rechnungErzeugt || isDirty || sendMutation.isPending}
            title={
              !rechnungErzeugt
                ? "Erst Rechnung speichern & erzeugen"
                : isDirty
                ? "Ungespeicherte Änderungen — erst speichern & erzeugen"
                : "Rechnung per Mail versenden"
            }
          >
            Versenden
          </Btn>
        </div>

        {/* Body: split */}
        <div style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "minmax(340px, 420px) minmax(500px, 1fr)",
          minHeight: 0,
        }}>
          {/* ── LEFT: Builder ─────────────────────────────────── */}
          <div style={{
            overflow: "auto",
            padding: "16px 18px",
            borderRight: "1px solid var(--border)",
            background: "var(--bg-card)",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}>
            {/* Hinweis-Block (Intern, Storno-Regel, etc.) */}
            {positionenData?.hinweis && (
              <div style={INFO_STYLE}>
                <div style={{ fontWeight: 600, color: "var(--fg)", marginBottom: 4 }}>Hinweis</div>
                <div>{positionenData.hinweis}</div>
              </div>
            )}

            {/* Meta */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Saison">
                <select value={saison} onChange={(e) => setSaison(e.target.value)} style={INPUT_STYLE}>
                  <option value="Sommer">Sommer</option>
                  <option value="Winter">Winter</option>
                </select>
              </Field>
              <Field label="Gruppenart">
                <select value={gruppenart} onChange={(e) => setGruppenart(e.target.value)} style={INPUT_STYLE}>
                  <option value="">— bitte wählen —</option>
                  {GRUPPENARTEN.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
            </div>

            {/* Positionen-Tabelle (Backend) */}
            <div>
              <div style={LABEL_STYLE}>Positionen</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid var(--fg)" }}>
                    <th style={{ ...TH, width: 20 }}>#</th>
                    <th style={TH}>Bezeichnung</th>
                    <th style={{ ...TH, textAlign: "right", width: 80 }}>Gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let visualIdx = 0;
                    return positionen.map((p, i) => {
                      if (p.kind === "hinweis") {
                        return (
                          <tr key={i}><td colSpan={3} style={{ ...TD, fontStyle: "italic", color: "var(--fg-muted)" }}>
                            {p.bezeichnung}
                          </td></tr>
                        );
                      }
                      visualIdx += 1;
                      const negativ = p.gesamt < 0;
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={TD}>{visualIdx}</td>
                          <td style={TD}>
                            <div style={{ fontWeight: 500 }}>{p.bezeichnung}</div>
                            {p.detail && <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 1 }}>{p.detail}</div>}
                            {(p.menge_text || p.einzelpreis_text) && (
                              <div style={{ fontSize: 10, color: "var(--fg-muted)" }}>
                                {[p.menge_text, p.einzelpreis_text].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </td>
                          <td style={{
                            ...TD, textAlign: "right", fontWeight: 500,
                            color: negativ ? "var(--status-cancel, #c0392b)" : undefined,
                          }}>
                            {formatEuro(p.gesamt)}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                  {positionen.length === 0 && !previewLoading && (
                    <tr><td colSpan={3} style={{ ...TD, color: "var(--fg-muted)", fontStyle: "italic" }}>
                      Kein Preis berechnet
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Summen-Block */}
            <div style={{ padding: "8px 0", borderTop: "1px solid var(--border)", fontSize: 12 }}>
              <SumRow label="Zwischensumme" value={summen.zwischensumme} />
              {summen.rabattBetrag > 0 && (
                <SumRow label={`Rabatt (${rabattProzent} %)`} value={-summen.rabattBetrag} />
              )}
              {summen.anzahlung > 0 && (
                <SumRow label="− Anzahlung erhalten" value={-summen.anzahlung} />
              )}
              <SumRow label="Gesamtbetrag" value={summen.gesamt} fett gross />
            </div>

            {/* Nachreinigung (nur Normal- und Storno-Fall sinnvoll, nicht bei Intern) */}
            {!isIntern && (
              <>
                <Field label="Nachreinigung / Fehlteile (€)">
                  <EurInput value={nachreinigungEur} onChange={setNachreinigungEur} style={INPUT_STYLE} />
                </Field>
                {nachreinigungEur > 0 && (
                  <Field label="Fehlteile / Schäden (Beschreibung)">
                    <textarea value={fehlteile} onChange={(e) => setFehlteile(e.target.value)}
                      placeholder="z. B. 2 Handtücher fehlen, Teller zerbrochen …"
                      style={{ ...INPUT_STYLE, minHeight: 54, resize: "vertical" }} />
                  </Field>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 10 }}>
                  <Field label="Rabatt %">
                    <input
                      type="number"
                      min={0} max={100} step={1}
                      value={rabattProzent || ""}
                      onChange={(e) => setRabattProzent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                      style={INPUT_STYLE}
                    />
                  </Field>
                  <Field label="Rabatt-Grund (optional)">
                    <input
                      type="text"
                      value={rabattGrund}
                      onChange={(e) => setRabattGrund(e.target.value)}
                      placeholder="z. B. Pfadfinder-Rabatt"
                      style={INPUT_STYLE}
                    />
                  </Field>
                </div>
              </>
            )}

            {/* Notiz */}
            <Field label="Notiz auf Rechnung">
              <textarea value={bemerkung} onChange={(e) => setBemerkung(e.target.value)}
                placeholder={isStorno ? "z. B. Hinweis zur Stornierung …" : "z. B. Vielen Dank für Euren Aufenthalt …"}
                style={{ ...INPUT_STYLE, minHeight: 64, resize: "vertical" }} />
            </Field>

            {/* Anzahlung Info */}
            <div style={INFO_STYLE}>
              {bk.anzahlung_erhalten
                ? <>Anzahlung erhalten: <strong>{formatEuro(bk.anzahlung_betrag ?? 100)}</strong> — wird abgezogen.</>
                : <>Keine Anzahlung hinterlegt (wird nicht abgezogen).</>}
            </div>

            {keinPreis && (
              <div style={{ ...INFO_STYLE, borderLeftColor: "var(--status-cancel, #c0392b)", background: "rgba(192,57,43,.08)", color: "var(--status-cancel, #c0392b)" }}>
                Kein Unterkunftspreis ermittelbar. Tarif für Einheit / Saison / Gasttyp prüfen.
              </div>
            )}

            {rechnungErzeugt && isDirty && (
              <div style={{ ...INFO_STYLE, borderLeftColor: "var(--status-warn, #d97706)", background: "rgba(217,119,6,.08)", color: "var(--status-warn, #d97706)" }}>
                Ungespeicherte Änderungen — die in der Ablage liegende Rechnung ist nicht mehr aktuell.
                Bitte zuerst <strong>Speichern &amp; erzeugen</strong>, bevor versendet wird.
              </div>
            )}
          </div>

          {/* ── RIGHT: Live-Vorschau ───────────────────────────── */}
          <div style={{
            overflow: "hidden",
            background: "#e8e6e1",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}>
            {previewLoading && (
              <div style={{
                position: "absolute", top: 8, right: 12, zIndex: 10,
                fontSize: 11, color: "var(--fg-muted)",
                background: "var(--bg-card)", padding: "2px 8px",
                borderRadius: 6, border: "1px solid var(--border)",
              }}>
                Aktualisiere …
              </div>
            )}
            <iframe
              ref={iframeRef}
              title="Rechnungsvorschau"
              style={{ flex: 1, border: "none", width: "100%", height: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>

    {showSmtpWarn && (
      <div
        onClick={() => setShowSmtpWarn(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 400,
          background: "rgba(20,15,10,.5)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "var(--bg-page)", borderRadius: 12, overflow: "hidden",
            width: "min(460px, 96vw)",
            boxShadow: "0 24px 60px rgba(0,0,0,.3)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--bg-card)" }}>
            <span className="material-icons-outlined" style={{ fontSize: 22, color: "var(--status-warn, #d97706)" }}>warning</span>
            <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>SMTP nicht vollständig konfiguriert</div>
          </div>
          <div style={{ padding: "16px 20px", fontSize: 13, color: "var(--fg-muted)", lineHeight: 1.6 }}>
            Der SMTP-Versand ist aktiviert, aber <strong style={{ color: "var(--fg)" }}>Server oder Absender</strong> fehlen noch.
            Bitte zuerst die{" "}
            <a href="/app/settings" style={{ color: "var(--brand-700)" }}>Mail-Einstellungen</a>
            {" "}vervollständigen.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 20px", borderTop: "1px solid var(--border)", background: "var(--bg-card)" }}>
            <Btn variant="ghost" size="sm" onClick={() => setShowSmtpWarn(false)}>Abbrechen</Btn>
            <Btn variant="secondary" size="sm" onClick={() => { setShowSmtpWarn(false); setShowMailPreview(true); }}>Trotzdem öffnen</Btn>
          </div>
        </div>
      </div>
    )}
    {showMailPreview && (
      <MailPreviewModal
        bookingId={bk.id}
        art="rechnung"
        onClose={() => { setShowMailPreview(false); onClose(); }}
        onToast={onToast}
      />
    )}
    </>
  );
}

// ── Hilfskomponenten ──────────────────────────────────────────────────────────

function SumRow({ label, value, fett, gross }: { label: string; value: number; fett?: boolean; gross?: boolean }) {
  const fs = gross ? 14 : 12;
  const fw = fett ? 700 : 400;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", fontSize: fs, fontWeight: fw }}>
      <span style={{ color: fett ? "var(--fg)" : "var(--fg-muted)" }}>{label}</span>
      <span style={{ color: value < 0 ? "var(--status-cancel, #c0392b)" : "var(--fg)" }}>{formatEuro(value)}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <div style={LABEL_STYLE}>{label}</div>
      {children}
    </label>
  );
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em",
  color: "var(--fg-muted)", fontWeight: 600, marginBottom: 3,
};

const INPUT_STYLE: React.CSSProperties = {
  width: "100%", padding: "5px 8px",
  border: "1px solid var(--border-strong, var(--border))",
  borderRadius: 6, fontFamily: "var(--font-body)", fontSize: 13,
  color: "var(--fg)", background: "var(--bg-card)",
  outline: "none", boxSizing: "border-box",
};

const TH: React.CSSProperties = {
  fontSize: 9, textTransform: "uppercase", letterSpacing: ".05em",
  color: "var(--fg-muted)", fontWeight: 700, padding: "3px 4px",
  textAlign: "left",
};

const TD: React.CSSProperties = {
  padding: "4px 4px", verticalAlign: "top",
};

const INFO_STYLE: React.CSSProperties = {
  fontSize: 12, color: "var(--fg-muted)", padding: "8px 10px",
  background: "var(--bg-subtle)", borderRadius: 6,
  borderLeft: "3px solid var(--brand-700)",
};
